import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, Clipboard } from 'lucide-react';
import { Doctor, Staff, ScheduleItem } from './SchedulerTypes';
import { formatDateToString, isToday } from '../lib/dateUtils';

interface DayInfo {
  date: Date;
  isCurrentMonth: boolean;
}

interface WeekData {
  days: DayInfo[];
  maxHeight: number;
}

interface PlanningMonthlyViewProps {
  calendarDays: DayInfo[];
  scheduleItems: ScheduleItem[];
  doctors: Doctor[];
  staff: Staff[];
  getDoctorById: (id: string) => Doctor | undefined;
  getStaffById: (id: string) => Staff | undefined;
  onDayClick: (date: Date) => void;
  onCopyScheduleItems: (dateString: string) => void;
  onPasteScheduleItems: (dateString: string) => void;
  copiedItems: ScheduleItem[];
  onDoctorClick: (scheduleItem: ScheduleItem, e: React.MouseEvent, allSchedulesForDay?: ScheduleItem[]) => void;
  onStaffClick: (scheduleItem: ScheduleItem, e: React.MouseEvent, allSchedulesForDay?: ScheduleItem[]) => void;
}

interface DoctorStaffPairing {
  doctor: Doctor;
  staffMembers: Staff[];
  scheduleItems: ScheduleItem[];
  isLeave: boolean;
  isMC: boolean;
}

interface LeaveInfo {
  person: Doctor | Staff;
  isMC: boolean;
  isDoctorLeave: boolean;
  scheduleItem: ScheduleItem;
}

interface StaffWorkInfo {
  staff: Staff;
  scheduleItem: ScheduleItem;
  scheduleItems: ScheduleItem[];
}

const PlanningMonthlyView: React.FC<PlanningMonthlyViewProps> = ({
  calendarDays,
  scheduleItems,
  doctors,
  staff,
  getDoctorById,
  getStaffById,
  onDayClick,
  onCopyScheduleItems,
  onPasteScheduleItems,
  copiedItems,
  onDoctorClick,
  onStaffClick,
}) => {
  // Process schedules for a specific day to get doctor-staff pairings and leave info
  const processDaySchedules = (dateString: string): {
    pairings: DoctorStaffPairing[],
    leaves: LeaveInfo[],
    staffWorks: StaffWorkInfo[]
  } => {
    const daySchedules = scheduleItems.filter(item => item.date === dateString);

    // Separate leave schedules from regular schedules
    const leaveSchedules = daySchedules.filter(item => item.type === 'leave' || item.type === 'mc');
    const regularSchedules = daySchedules.filter(item => item.type !== 'leave' && item.type !== 'mc');

    // Process regular schedules - group by doctor
    const doctorMap = new Map<string, Set<string>>();
    // Track staff work (no doctor assigned)
    const staffWorkMap = new Map<string, ScheduleItem[]>();

    regularSchedules.forEach(item => {
      if (item.doctorId) {
        if (!doctorMap.has(item.doctorId)) {
          doctorMap.set(item.doctorId, new Set());
        }
        // Add staff if present
        if (item.staffId) {
          doctorMap.get(item.doctorId)?.add(item.staffId);
        }
      } else if (item.staffId) {
        // Staff schedule without doctor (Admin/Other)
        if (!staffWorkMap.has(item.staffId)) {
          staffWorkMap.set(item.staffId, []);
        }
        staffWorkMap.get(item.staffId)?.push(item);
      }
    });

    // Convert to pairings array
    const pairings: DoctorStaffPairing[] = Array.from(doctorMap.entries()).map(([doctorId, staffIds]) => {
      const doctor = getDoctorById(doctorId);
      const staffMembers = Array.from(staffIds)
        .map(staffId => getStaffById(staffId))
        .filter((s): s is Staff => s !== undefined);

      // Get ALL schedule items for this doctor on this day (for modal)
      const doctorSchedules = regularSchedules.filter(item => item.doctorId === doctorId);

      return {
        doctor: doctor!,
        staffMembers,
        scheduleItems: doctorSchedules,
        isLeave: false,
        isMC: false,
      };
    }).filter(p => p.doctor); // Remove entries where doctor is not found

    // Collect all staff IDs that appear in doctor pairings
    const staffInPairings = new Set<string>();
    doctorMap.forEach((staffIds) => {
      staffIds.forEach(staffId => staffInPairings.add(staffId));
    });

    // Process staff work (Admin/Other) - exclude staff already shown in pairings
    const staffWorks: StaffWorkInfo[] = Array.from(staffWorkMap.entries())
      .filter(([staffId]) => !staffInPairings.has(staffId))
      .map(([staffId, items]) => {
        const staffMember = getStaffById(staffId);
        return {
          staff: staffMember!,
          scheduleItem: items[0], // Primary item for click
          scheduleItems: items
        };
      }).filter(s => s.staff);

    // Process leave schedules - deduplicate by person but store schedule item for modal
    const leaveMap = new Map<string, { person: Doctor | Staff, hasMC: boolean, isDoctorLeave: boolean, scheduleItem: ScheduleItem }>();

    leaveSchedules.forEach(item => {
      const doctor = item.doctorId ? getDoctorById(item.doctorId) : null;
      const staffMember = item.staffId ? getStaffById(item.staffId) : null;
      const person = doctor || staffMember;

      if (!person) return;

      const personKey = person.id;
      const existing = leaveMap.get(personKey);
      const isMC = item.type === 'mc';

      if (!existing) {
        leaveMap.set(personKey, {
          person,
          hasMC: isMC,
          isDoctorLeave: !!doctor,
          scheduleItem: item
        });
      } else if (isMC && !existing.hasMC) {
        // If this is MC and existing wasn't, update to show MC and replace schedule item
        existing.hasMC = true;
        existing.scheduleItem = item;
      }
    });

    // Convert map to array
    const leaves: LeaveInfo[] = Array.from(leaveMap.values()).map(({ person, hasMC, isDoctorLeave, scheduleItem }) => ({
      person,
      isMC: hasMC,
      isDoctorLeave,
      scheduleItem
    }));

    // Sort pairings by doctor name
    pairings.sort((a, b) => {
      const nameA = a.doctor.short_name || a.doctor.name;
      const nameB = b.doctor.short_name || b.doctor.name;
      return nameA.localeCompare(nameB);
    });

    // Sort leaves by type (MC first) then by name
    leaves.sort((a, b) => {
      if (a.isMC !== b.isMC) return a.isMC ? -1 : 1;
      const nameA = a.person.short_name || a.person.name;
      const nameB = b.person.short_name || b.person.name;
      return nameA.localeCompare(nameB);
    });

    // Sort staff works by name
    staffWorks.sort((a, b) => {
      const nameA = a.staff.short_name || a.staff.name;
      const nameB = b.staff.short_name || b.staff.name;
      return nameA.localeCompare(nameB);
    });

    return { pairings, leaves, staffWorks };
  };

  // Group calendar days into weeks and calculate max height for each week
  const weekRows = useMemo((): WeekData[] => {
    // Calculate height needed for a day's content
    const calculateDayHeight = (dateString: string): number => {
      const { pairings, leaves, staffWorks } = processDaySchedules(dateString);

      const MIN_HEIGHT = 180; // minimum height in pixels
      const LINE_HEIGHT = 26; // text-xs with p-1 padding
      const HEADER_HEIGHT = 44; // day number + copy/paste buttons
      const SECTION_HEADER_HEIGHT = 16; // "Leave" or "Admin" text (text-[10px])
      const SECTION_BORDER_PADDING = 8; // pt-1 border-t mt-1
      const PADDING = 12; // p-2 on CardContent (top + bottom)

      const pairingHeight = pairings.length * LINE_HEIGHT;

      const leaveHeight = leaves.length > 0
        ? (SECTION_HEADER_HEIGHT + (leaves.length * LINE_HEIGHT) + SECTION_BORDER_PADDING)
        : 0;

      const staffWorkHeight = staffWorks.length > 0
        ? (SECTION_HEADER_HEIGHT + (staffWorks.length * LINE_HEIGHT) + (staffWorks.length > 1 ? (staffWorks.length - 1) * 4 : 0) + SECTION_BORDER_PADDING)
        : 0;

      const totalContentHeight = HEADER_HEIGHT + pairingHeight + leaveHeight + staffWorkHeight + PADDING;

      return Math.max(MIN_HEIGHT, totalContentHeight);
    };

    const weeks: WeekData[] = [];

    for (let i = 0; i < calendarDays.length; i += 7) {
      const weekDays = calendarDays.slice(i, i + 7);

      // Calculate height for each day in the week
      const heights = weekDays.map(dayObj => {
        const dateString = formatDateToString(dayObj.date);
        return calculateDayHeight(dateString);
      });

      // Find maximum height for this week
      const maxHeight = Math.max(...heights, 180); // Ensure at least minimum height

      weeks.push({
        days: weekDays,
        maxHeight
      });
    }

    return weeks;
  }, [calendarDays, scheduleItems]);

  return (
    <div className="overflow-x-auto -mx-2 px-2" style={{ WebkitOverflowScrolling: 'touch' }}>
      <div className="min-w-[500px]">
      {/* Sticky day headers */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2 sticky top-0 z-30 bg-background pb-2 border-b">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayName) => (
          <div key={dayName} className="p-1 sm:p-2 text-center text-xs sm:text-sm font-medium text-muted-foreground bg-muted/50">
            {dayName}
          </div>
        ))}
      </div>

      {/* Calendar grid - render by weeks */}
      <div className="pt-2 space-y-2">
        {weekRows.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 gap-1 sm:gap-2">
            {week.days.map((dayObj, dayIndex) => {
              const day = dayObj.date;
              const isCurrentMonth = dayObj.isCurrentMonth;
              const isTodayDate = isToday(day);
              const dateString = formatDateToString(day);

              const { pairings, leaves, staffWorks } = processDaySchedules(dateString);

              return (
                <Card
                  key={dayIndex}
                  className={`cursor-pointer transition-colors hover:bg-muted/50 ${!isCurrentMonth ? 'opacity-50' : ''
                    } ${isTodayDate ? 'ring-2 ring-primary' : ''}`}
                  style={{ height: `${week.maxHeight}px` }}
                  onClick={() => onDayClick(day)}
                >
                  <CardHeader className="p-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className={`text-sm ${isTodayDate ? 'text-primary font-bold' : ''}`}>
                        {day.getDate()}
                      </CardTitle>
                      <div className="flex space-x-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-4 w-4 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCopyScheduleItems(dateString);
                          }}
                          title="Copy day schedule"
                        >
                          <Copy className="h-2 w-2" />
                        </Button>
                        {copiedItems.length > 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-4 w-4 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              onPasteScheduleItems(dateString);
                            }}
                            title={`Paste ${copiedItems.length} items`}
                          >
                            <Clipboard className="h-2 w-2" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="p-2 pt-0">
                    <div className="space-y-1">
                      {/* Doctor-Staff Pairings */}
                      {pairings.map((pairing, idx) => {
                        const doctorName = pairing.doctor.short_name || pairing.doctor.name;
                        const staffNames = pairing.staffMembers.length > 0
                          ? pairing.staffMembers.map(s => s.short_name || s.name).join(', ')
                          : '';

                        // Use first schedule item as primary, pass all schedules to modal
                        const primarySchedule = pairing.scheduleItems[0];

                        return (
                          <div
                            key={idx}
                            className="text-xs p-1 rounded cursor-pointer hover:opacity-70 transition-opacity"
                            style={{
                              backgroundColor: `${pairing.doctor.color}15`,
                              borderLeft: `3px solid ${pairing.doctor.color}`,
                            }}
                            onClick={(e) => {
                              if (primarySchedule) {
                                onDoctorClick(primarySchedule, e, pairing.scheduleItems);
                              }
                            }}
                          >
                            <div className="font-medium flex items-center gap-1">
                              <span style={{ color: pairing.doctor.color }}>{doctorName}</span>
                              <span className="text-muted-foreground">|</span>
                              {staffNames && (
                                <span className="text-muted-foreground">{staffNames}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {/* Leave Section */}
                      {leaves.length > 0 && (
                        <div className="pt-1 border-t border-muted mt-1">
                          <div className="text-[10px] font-semibold text-red-600 mb-0.5">
                            Leave
                          </div>
                          {leaves.map((leave, idx) => {
                            const personName = leave.person.short_name || leave.person.name;
                            const bgColor = leave.isMC ? '#b91c1c' : '#bfdbfe';
                            const textColor = leave.isMC ? '#ffffff' : '#1e3a8a';

                            return (
                              <div
                                key={idx}
                                className="text-xs px-1 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity"
                                style={{ backgroundColor: bgColor, color: textColor }}
                                onClick={(e) => {
                                  // Open appropriate modal based on whether it's doctor or staff leave
                                  if (leave.isDoctorLeave) {
                                    onDoctorClick(leave.scheduleItem, e);
                                  } else {
                                    onStaffClick(leave.scheduleItem, e);
                                  }
                                }}
                              >
                                {personName}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Staff Admin/Other Section */}
                      {staffWorks.length > 0 && (
                        <div className="pt-1 border-t border-muted mt-1">
                          <div className="text-[10px] font-semibold text-gray-600 mb-0.5">
                            Admin / Other
                          </div>
                          <div className="space-y-1">
                            {staffWorks.map((work, idx) => {
                              const staffName = work.staff.short_name || work.staff.name;

                              return (
                                <div
                                  key={idx}
                                  className="text-xs px-1 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity bg-gray-100 text-gray-800"
                                  onClick={(e) => {
                                    onStaffClick(work.scheduleItem, e, work.scheduleItems);
                                  }}
                                >
                                  {staffName}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Empty state */}
                      {pairings.length === 0 && leaves.length === 0 && staffWorks.length === 0 && (
                        <div className="text-xs text-muted-foreground text-center py-4">
                          No schedules
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ))}
      </div>
      </div>
    </div>
  );
};

export default PlanningMonthlyView;
